import type { FormEvent } from 'react'
import { useState } from 'react'
import { Button, Card, EmptyState, Skeleton, useToast } from '../../ui'
import { api, post } from '../../lib/api'
import { useSession } from '../../lib/session'
import { Pill, errText, when, useAdmin } from './shared'
import styles from './admin.module.css'

interface UserRow {
  id: number; email: string | null; username: string; display_name: string | null; role: string; status: string
  created_at: string; last_login_at: string | null; last_seen_at: string | null; sessions: number; plan: string | null
  telegram: boolean; holdings: number; watch_items: number; ranked_levels: boolean
}
interface Users { data: UserRow[]; total: number; page: number; page_size: number }

export function UsersTab() {
  const toast = useToast()
  const { me } = useSession()
  const [q, setQ] = useState('')
  const [applied, setApplied] = useState({ q: '', role: '', status: '', page: 1 })
  const qs = new URLSearchParams()
  if (applied.q) qs.set('q', applied.q)
  if (applied.role) qs.set('role', applied.role)
  if (applied.status) qs.set('status', applied.status)
  if (applied.page > 1) qs.set('page', String(applied.page))
  const { data, error, reload } = useAdmin<Users>(`/admin/users${qs.toString() ? `?${qs}` : ''}`)
  const [busy, setBusy] = useState<number | null>(null)

  const change = async (u: UserRow, body: { role?: string; status?: string; ranked_levels?: boolean }, what: string) => {
    if (!window.confirm(`${what} for ${u.email ?? u.username}?`)) return
    setBusy(u.id)
    try { await api(`/admin/users/${u.id}`, { method: 'PATCH', body: JSON.stringify(body) }); toast.show({ tone: 'success', message: `${u.email ?? u.username}: ${what.toLowerCase()} done` }); await reload() }
    catch (e) { toast.show({ tone: 'error', message: errText(e) }) } finally { setBusy(null) }
  }
  const signOut = async (u: UserRow) => {
    if (!window.confirm(`Sign ${u.email ?? u.username} out of every device?`)) return
    setBusy(u.id)
    try { const r = await post<{ revoked: number }>(`/admin/users/${u.id}/revoke-sessions`, {}); toast.show({ tone: 'success', message: `${r.revoked} session(s) ended` }); await reload() }
    catch (e) { toast.show({ tone: 'error', message: errText(e) }) } finally { setBusy(null) }
  }
  const search = (e: FormEvent) => { e.preventDefault(); setApplied(a => ({ ...a, q: q.trim(), page: 1 })) }
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1

  return (
    <div className={styles.section}>
      <form onSubmit={search} className={styles.toolbar} role="search" aria-label="Find users">
        <label htmlFor="admin-user-q" className="sr-only">Search users</label>
        <input id="admin-user-q" className={styles.input} style={{ minWidth: 260 }} placeholder="Email, username or name" value={q} onChange={e => setQ(e.target.value)} />
        <label htmlFor="admin-user-role" className="sr-only">Role</label>
        <select id="admin-user-role" className={styles.select} value={applied.role} onChange={e => setApplied(a => ({ ...a, role: e.target.value, page: 1 }))}>
          <option value="">All roles</option><option value="admin">Admins</option><option value="user">Members</option>
        </select>
        <label htmlFor="admin-user-status" className="sr-only">Status</label>
        <select id="admin-user-status" className={styles.select} value={applied.status} onChange={e => setApplied(a => ({ ...a, status: e.target.value, page: 1 }))}>
          <option value="">Any status</option><option value="active">Active</option><option value="suspended">Suspended</option>
        </select>
        <Button type="submit" variant="primary">Search</Button>
        <span className={styles.spacer} />
        {data && <span className={styles.muted}>{data.total} user(s)</span>}
      </form>

      <Card variant="flush">
        {error && !data ? <EmptyState kind="error" title="Could not load users" compact>{error}</EmptyState>
          : !data ? <div style={{ padding: 16 }}><Skeleton variant="row" lines={5} label="Loading users" /></div>
          : data.data.length === 0 ? <EmptyState title="No users match" compact />
          : (
            <>
              <div className={styles.tableWrap} tabIndex={0} role="region" aria-label="Users (scrolls sideways)">
                <table className={styles.table}><caption className="sr-only">Users</caption>
                  <thead><tr><th scope="col">User</th><th scope="col">Role</th><th scope="col">Status</th><th scope="col">Plan</th><th scope="col">Joined</th><th scope="col">Last seen</th><th scope="col" className={styles.thNum}>Watch / hold</th><th scope="col">Telegram</th><th scope="col">Full Ranked</th><th scope="col">Actions</th></tr></thead>
                  <tbody>{data.data.map(u => {
                    const self = u.id === me?.user.id
                    return (
                      <tr key={u.id}>
                        <td><b>{u.email ?? u.display_name ?? u.username}</b>{u.email ? null : <span className={styles.muted}> (no email — legacy login)</span>}<br /><span className={styles.muted}>#{u.id} · {u.display_name ?? u.username} · {u.sessions} session(s)</span></td>
                        <td>{u.role === 'admin' ? <Pill tone="warn">Admin</Pill> : 'Member'}</td>
                        <td>{u.status === 'active' ? <Pill tone="ok">Active</Pill> : <Pill tone="bad">{u.status}</Pill>}</td>
                        <td>{u.plan ?? '—'}</td>
                        <td className={styles.muted}>{when(u.created_at)}</td>
                        <td className={styles.muted}>{when(u.last_seen_at ?? u.last_login_at)}</td>
                        <td className={styles.num}>{u.watch_items} / {u.holdings}</td>
                        <td>{u.telegram ? 'Linked' : '—'}</td>
                        <td>{u.role === 'admin' ? <span className={styles.muted}>Admin (always)</span> : (
                          <button type="button" className={styles.linkBtn} disabled={busy === u.id}
                            onClick={() => void change(u, { ranked_levels: !u.ranked_levels }, u.ranked_levels ? 'Remove Full Ranked access' : 'Invite to Full Ranked (entry/stop-loss/targets)')}>
                            {u.ranked_levels ? <><Pill tone="ok">Invited</Pill> Remove</> : 'Invite'}
                          </button>
                        )}</td>
                        <td>{self ? <span className={styles.muted}>You</span> : (
                          <span className={styles.actionsCell}>
                            <button type="button" className={styles.linkBtn} disabled={busy === u.id} onClick={() => void change(u, { role: u.role === 'admin' ? 'user' : 'admin' }, u.role === 'admin' ? 'Remove admin' : 'Make admin')}>{u.role === 'admin' ? 'Remove admin' : 'Make admin'}</button>
                            <button type="button" className={`${styles.linkBtn} ${u.status === 'active' ? styles.danger : ''}`} disabled={busy === u.id} onClick={() => void change(u, { status: u.status === 'active' ? 'suspended' : 'active' }, u.status === 'active' ? 'Suspend' : 'Reactivate')}>{u.status === 'active' ? 'Suspend' : 'Reactivate'}</button>
                            <button type="button" className={styles.linkBtn} disabled={busy === u.id || u.sessions === 0} onClick={() => void signOut(u)}>Sign out</button>
                          </span>
                        )}</td>
                      </tr>
                    )
                  })}</tbody>
                </table>
              </div>
              {pages > 1 && (
                <div className={styles.pager}>
                  <button type="button" className={styles.linkBtn} disabled={applied.page <= 1} onClick={() => setApplied(a => ({ ...a, page: a.page - 1 }))}>← Previous</button>
                  <span className={styles.muted}>Page {applied.page} of {pages}</span>
                  <button type="button" className={styles.linkBtn} disabled={applied.page >= pages} onClick={() => setApplied(a => ({ ...a, page: a.page + 1 }))}>Next →</button>
                </div>
              )}
            </>
          )}
      </Card>
    </div>
  )
}
